import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, GraduationCapIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { readMatchParams, readProfileParams } from '../utils/matchParams';
import { sanitizeTcpaHtml } from '../utils/sanitizeHtml';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ACCENT, SelectField, StageProgress, StepCard } from '../components/form/FormStepUI';
import { TCPADisclosure } from '../components/TCPADisclosure';
import { TrackingNotice } from '../components/TrackingNotice';
import { trackLeadConversion } from '../utils/conversion';
import { areaFromProgram, clicksSearch, leadsSearch, tcpaPost, tcpaSearch, type ContactProfile, type MMSchool, type SearchProfile, type TCPAResult } from '../services/mediaMatchers';
// Step option lists
// Labels match the partner's value mapping, so the codes sent are unambiguous.
const educationLevels = ['High School Diploma / GED', 'Some High School', 'Some College', "Associate's Degree", "Bachelor's Degree", "Master's Degree", 'Doctorate'];
function getGradYearOptions(): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  // Anything older than 60 years is rejected by the partner, so the list stops
  // there and collapses into a single "Before 1965" option.
  for (let y = currentYear + 2; y >= currentYear - 60; y--) {
    years.push(String(y));
  }
  years.push('Before 1965');
  return years;
}
const gradYearOptions = getGradYearOptions();
const startTimeframes = ['Immediately', '1–3 months', '3–6 months', '6–12 months', 'More than a year', 'Undecided'];
export function RequestInformationForm() {
  usePageMeta('Request Information | Find Online Degree Programs', 'Complete a quick form to get matched with accredited online degree programs.', {
    noindex: true
  });
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state || {}) as {
    degreeInterest?: string;
    areaOfStudy?: string;
    specialization?: string;
  };
  // Fall back to URL params so a direct anchor link lands here pre-filled.
  const urlPrefill = readMatchParams();
  const profilePrefill = readProfileParams();
  const prefill = {
    degreeInterest: navState.degreeInterest || urlPrefill.degreeInterest,
    // The area is derivable from the program id when it isn't given.
    areaOfStudy: navState.areaOfStudy || urlPrefill.areaOfStudy || areaFromProgram(urlPrefill.specialization),
    specialization: navState.specialization || urlPrefill.specialization,
    ...profilePrefill
  };
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep3
  } = useFormState(prefill);
  const [step, setStep] = useState(1);
  const [zipLoading, setZipLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepError, setStepError] = useState<string>('');
  // Mounts the LeadiD and TrustedForm scripts, so their hidden inputs are
  // populated before any Media Matchers call fires.
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();

  // --- Media Matchers state -------------------------------------------------
  /** The PII step. The searches must resolve before it renders. */
  const CONTACT_STEP = 5;
  /** Location — the last step that contributes to the search payload. */
  const LOCATION_STEP = 4;
  const [leads, setLeads] = useState<MMSchool[] | null>(null);
  const [tcpa, setTcpa] = useState<TCPAResult | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [tcpaFailed, setTcpaFailed] = useState(false);
  const searchFired = useRef(false);
  const buildProfile = useCallback((): SearchProfile => ({
    degreeInterest: formData.degreeInterest,
    areaStudy: formData.areaOfStudy,
    mProgram: formData.specialization,
    currentEducation: formData.currentEducation,
    gradYear: formData.gradYear,
    startTimeframe: formData.startDate,
    militaryAffiliation: formData.isMilitary,
    zipCode: formData.zipCode.replace(/\D/g, ''),
    address: formData.address,
    city: formData.city,
    state: formData.state
  }), [formData]);
  const runSearches = useCallback(async () => {
    setSearchLoading(true);
    setTcpaFailed(false);
    const profile = buildProfile();
    // Both fire in parallel — neither depends on the other.
    const [leadsResult, tcpaResult] = await Promise.all([leadsSearch(profile), tcpaSearch(profile)]);
    setLeads(leadsResult || []);
    setTcpa(tcpaResult);
    if (!tcpaResult) setTcpaFailed(true);
    setSearchLoading(false);
  }, [buildProfile]);

  // Safety net: if the visitor reaches the PII step without the searches
  // having run (a restored session, say), fire them on arrival.
  useEffect(() => {
    if (step !== CONTACT_STEP || searchFired.current) return;
    searchFired.current = true;
    runSearches();
  }, [step, runSearches]);
  const retrySearches = () => {
    searchFired.current = true;
    runSearches();
  };
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
        }
      }
    } catch {
      /* ignore */
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const goBack = () => {
    setStepError('');
    if (step === 1) {
      navigate('/get-matched');
    } else {
      setStep(step - 1);
    }
  };
  const goNext = () => {
    setStepError('');
    // Per-step validation
    if (step === 1 && !formData.currentEducation) {
      setStepError('Please select your highest level of education.');
      return;
    }
    if (step === 2 && !formData.gradYear) {
      setStepError('Please select your graduation year.');
      return;
    }
    if (step === 3 && !formData.startDate) {
      setStepError('Please select when you would like to start.');
      return;
    }
    if (step === LOCATION_STEP) {
      if (!formData.address.trim()) {
        setStepError('Please enter your street address.');
        return;
      }
      if (!formData.zipCode.trim() || formData.zipCode.replace(/\D/g, '').length < 5) {
        setStepError('Please enter a valid 5-digit ZIP code.');
        return;
      }
      // Location is the last input the search payload needs, so both Searches
      // fire here — before the PII step renders — and the TCPA disclaimer is
      // already in hand when the consent checkbox appears.
      if (!searchFired.current) {
        searchFired.current = true;
        runSearches();
      }
    }
    setStep(step + 1);
  };
  const handleSubmit = async () => {
    setStepError('');
    if (!validateUniStep3()) return;
    // Without the TCPA record there is no consent to post against.
    if (!tcpa) {
      setTcpaFailed(true);
      setStepError('We could not load the consent details. Please retry before submitting.');
      return;
    }
    setIsSubmitting(true);
    const profile = buildProfile();
    const contact: ContactProfile = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: stripPhone(formData.phone)
    };
    // TCPA Post first, then Clicks Search excluding the brand that just took
    // consent, so the carousel has results ready.
    const posted = await tcpaPost(tcpa.mRecordID, contact);
    // Conversion fires on confirmation, not on the click.
    if (posted) trackLeadConversion({
      source: 'tcpa'
    });
    const clicks = await clicksSearch(profile, contact, tcpa.mBrandID);
    setIsSubmitting(false);
    navigate('/matches', {
      state: {
        leads: leads || [],
        clicks: clicks || [],
        profile,
        contact,
        disclosure: tcpa.disclosure,
        excludeBrand: tcpa.mBrandID
      }
    });
  };
  // Card content for each step
  const renderStepCard = () => {
    if (step === 1) {
      return <StepCard title="What is your highest level of education?" onContinue={goNext} ctaLabel="Continue" info="Your education background helps schools understand where you are in your academic journey and recommend the right program level." error={stepError} data-id="element-2436" data-mp="1hch57uku9lu5">
          <SelectField value={formData.currentEducation} onChange={v => updateFormData('currentEducation', v)} options={educationLevels.map(l => ({
          label: l,
          value: l
        }))} data-id="element-2437" data-mp="3bkt7e0jty43w" />
        </StepCard>;
    }
    if (step === 2) {
      return <StepCard title="When did you graduate high school?" onContinue={goNext} ctaLabel="Continue" info="Knowing when you graduated helps us verify eligibility and connect you with programs that fit your timeline." error={stepError} data-id="element-2438" data-mp="2xqtkkz62dcwg">
          <SelectField value={formData.gradYear} onChange={v => updateFormData('gradYear', v)} options={gradYearOptions.map(y => ({
          label: y,
          value: y
        }))} data-id="element-2439" data-mp="3lk20ysas9gpq" />
        </StepCard>;
    }
    if (step === 3) {
      return <StepCard title="When would you like to start classes?" onContinue={goNext} ctaLabel="Continue" info="Your preferred start date helps us find programs with upcoming enrollment windows that work for your schedule." error={stepError} data-id="element-2440" data-mp="7xqzgw2dwfe0">
          <SelectField value={formData.startDate} onChange={v => updateFormData('startDate', v)} options={startTimeframes.map(t => ({
          label: t,
          value: t
        }))} data-id="element-2441" data-mp="bzkuoott8r6s" />
        </StepCard>;
    }
    if (step === 4) {
      return <StepCard title="Where are you located?" onContinue={goNext} ctaLabel="Continue" info="Your location helps us find programs available in your area and determine eligibility for state-specific financial aid." error={stepError} data-id="element-2442" data-mp="zp6sb7ujayco">
          <div className="space-y-5" data-id="element-2443" data-mp="2xf6sjq55b93e">
            <div data-id="element-2444" data-mp="2xg9i9uqqf5hq">
              <label htmlFor="address" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2445" data-mp="2xgrh8z4s3xtc">
                Street Address{' '}
                <span style={{
                color: ACCENT
              }} data-id="element-2446" data-mp="2rbjynkljswpu">
                  *
                </span>
              </label>
              <input id="address" type="text" value={formData.address} onChange={e => updateFormData('address', e.target.value)} placeholder="123 Main St" className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-0" style={{
              '--tw-ring-color': ACCENT
            } as React.CSSProperties} data-id="element-2447" data-mp="2iiqdj6jf7342" />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" style={{
            backgroundColor: "#FAFBFE"
          }} data-id="element-2448" data-mp="3tzq3vwm7j7vg">
              <label htmlFor="zipCode" className="flex items-center gap-2 text-sm font-bold text-gray-800 mb-2" data-id="element-2449" data-mp="3rlb9kaf4qoq7">
                <span data-id="element-2450" data-mp="3nhdbm31jjjyq">📍</span>
                Enter your ZIP Code
              </label>
              <div className="relative" data-id="element-2451" data-mp="3ikmwbhxk8sed">
                <input id="zipCode" type="text" value={formData.zipCode} onChange={e => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                updateFormData('zipCode', val);
                if (val.length === 5) lookupZip(val);
              }} placeholder="e.g. 90210" maxLength={5} className="block w-full px-4 py-3 text-base border-2 rounded-md bg-white focus:outline-none" style={{
                borderColor: ACCENT
              }} data-id="element-2452" data-mp="3ilofk9oqcl76" />
                {zipLoading && <div className="absolute right-3 top-1/2 -translate-y-1/2" data-id="element-2453" data-mp="f0d6rl2ec57">
                    <LoaderIcon className="w-4 h-4 animate-spin text-gray-500" data-id="element-2454" data-mp="xtcnbgegfpoc" />
                  </div>}
              </div>
              {formData.city && formData.state && <p className="text-xs text-gray-600 mt-2" data-id="element-2455" data-mp="t1l45qxmom88">
                  📌 {formData.city}, {formData.state}
                </p>}
            </div>
          </div>
        </StepCard>;
    }
    if (step === 5) {
      const locationLine = formData.city && formData.state ? `${formData.city}, ${formData.state}` : '';
      return <StepCard title="Last step! Where should we send your matches?" subtitle={`We'll match you with top programs in ${locationLine}.`} onContinue={handleSubmit} ctaLabel={isSubmitting ? 'Submitting...' : 'See Matching Colleges'} ctaIcon={<GraduationCapIcon className="w-5 h-5 ml-2 inline-block" data-id="element-2457" data-mp="1t26h7wyritos" />} isSubmitting={isSubmitting || searchLoading} error={stepError} data-id="element-2456" data-mp="1fccr7bbiu3dh">
          <div className="space-y-5" data-id="element-2458" data-mp="1y3vw5np3vri5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2459" data-mp="231ffh20nuugy">
              <div data-id="element-2460" data-mp="22z2u0evp21z5">
                <label htmlFor="firstName" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2461" data-mp="271zeyc8pia69">
                  First Name{' '}
                  <span style={{
                  color: ACCENT
                }} data-id="element-2462" data-mp="2zsiycqs927xh">
                    *
                  </span>
                </label>
                <input id="firstName" type="text" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} placeholder="John" className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.firstName ? 'border-red-400' : 'border-gray-300'}`} style={{
                '--tw-ring-color': ACCENT
              } as React.CSSProperties} data-id="element-2463" data-mp="39icb3v44ot3b" />
                {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-2464" data-mp="532s4kya8n8x">
                    {errors.firstName}
                  </p>}
              </div>
              <div data-id="element-2465" data-mp="hm85wejeefe2">
                <label htmlFor="lastName" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2466" data-mp="hmq5nl59rbqq">
                  Last Name{' '}
                  <span style={{
                  color: ACCENT
                }} data-id="element-2467" data-mp="8l9xzl5u9nq7">
                    *
                  </span>
                </label>
                <input id="lastName" type="text" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} placeholder="Doe" className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.lastName ? 'border-red-400' : 'border-gray-300'}`} style={{
                '--tw-ring-color': ACCENT
              } as React.CSSProperties} data-id="element-2468" data-mp="11aqm46mfeigm" />
                {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-2469" data-mp="1oydt50qbrlac">{errors.lastName}</p>}
              </div>
            </div>

            <div data-id="element-2470" data-mp="1fx65rdpck4v1">
              <label htmlFor="email" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2471" data-mp="1fxpatttweyrn">
                Email Address{' '}
                <span style={{
                color: ACCENT
              }} data-id="element-2472" data-mp="167csm89k3hoo">
                  *
                </span>
              </label>
              <input id="email" type="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} placeholder="john@example.com" className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.email ? 'border-red-400' : 'border-gray-300'}`} style={{
              '--tw-ring-color': ACCENT
            } as React.CSSProperties} data-id="element-2473" data-mp="115x7kg7dpb6a" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-2474" data-mp="37upcv7ac7db4">{errors.email}</p>}
            </div>

            <div data-id="element-2475" data-mp="2g5ch1zhrbnqo">
              <label htmlFor="phone" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2476" data-mp="2g5w05jj58i6a">
                Phone Number{' '}
                <span style={{
                color: ACCENT
              }} data-id="element-2477" data-mp="2p60zr7i3ic9w">
                  *
                </span>
              </label>
              <input id="phone" type="tel" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} placeholder="555-123-4567" maxLength={13} className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.phone ? 'border-red-400' : 'border-gray-300'}`} style={{
              '--tw-ring-color': ACCENT
            } as React.CSSProperties} data-id="element-2478" data-mp="2tylrm3hfyfas" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-2479" data-mp="1dq5iy9ixaife">{errors.phone}</p>}
            </div>

            {/* Consent language comes from the TCPA Search response, so the
                text shown is the text the certificate records. */}
            {searchLoading ? <div className="flex items-center gap-2 text-sm text-gray-500 pt-2" data-id="element-2480" data-mp="1ij8e2m3clzxp">
                <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-2481" data-mp="1wjj1tg9d2yen" />
                Loading consent details...
              </div> : tcpaFailed ? <div className="rounded-lg border border-red-200 bg-red-50 p-4" data-id="element-2482" data-mp="2b56phberwl29">
                <p className="text-sm text-red-800 font-medium" data-id="element-2483" data-mp="2b33o5v8675i8">
                  We couldn't load the consent details.
                </p>
                <p className="text-xs text-red-600 mt-1" data-id="element-2484" data-mp="2251llb8sg4su">
                  This is required before we can submit your request.
                </p>
                <button type="button" onClick={retrySearches} className="mt-3 inline-flex items-center px-4 py-2 rounded-md bg-white border border-red-300 text-sm font-bold text-red-700 hover:bg-red-100 transition-colors duration-200" data-id="element-2485" data-mp="264ae6cs3dwmt">
                  Retry
                </button>
              </div> : <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-1 h-4 w-4 rounded border-gray-300 cursor-pointer flex-shrink-0 accent-[#1668E0]" data-id="element-2486" data-mp="1nfvlzm61gckf">
                {/* tcpaDisclaimer is partner-supplied HTML, so it is
                    sanitized rather than rendered as plain text. */}
                {tcpa?.disclosure ? <span dangerouslySetInnerHTML={{
              __html: sanitizeTcpaHtml(tcpa.disclosure)
            }} data-id="element-2487" data-mp="13nfhymleb77h" /> : `I acknowledge that, by clicking the checkbox as my official
                signature, I consent to representatives of OnlineCollegeGuide and
                our Partner Schools contacting me about educational opportunities
                via email, text, or phone number(s), including through the use of
                an automatic dialer, at the number and email I provided. Message
                and data rates may apply. I understand that my consent is not a
                requirement to enroll, and I may withdraw my consent at any time.`}
              </TCPADisclosure>}
          </div>
        </StepCard>;
    }
    return null;
  };
  return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-2488" data-mp="2pazxx4m8smav">
      <Header data-id="element-2489" data-mp="332wce5uzmxos" />
      <main className="flex-1 py-6 sm:py-10 px-4" data-id="element-2490" data-mp="333fubmkn1m2t">
        <div className="max-w-3xl mx-auto" data-id="element-2491" data-mp="2y4mgft1fvt24">
          {/* Stage progress. Step 1 of the funnel is program selection on the
              start page, so the questionnaire begins at step 2. */}
          <StageProgress currentStep={step + 1} data-id="element-2492" data-mp="26e89yhvr9j0h" />

          {/* Back button */}
          <div className="mt-2 mb-4" data-id="element-2493" data-mp="2kdq0p2sm36ch">
            <button type="button" onClick={goBack} className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors" data-id="element-2494" data-mp="2kercj9hgbwv9">
              <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-2495" data-mp="3vrlikjawht7e" />
              Back
            </button>
          </div>

          {/* Step card */}
          <AnimatePresence mode="wait" data-id="element-2496" data-mp="3qw671y0d89e5">
            <motion.div key={step} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.2
          }} data-id="element-2497" data-mp="2o243imgmb6o">
              {renderStepCard()}
            </motion.div>
          </AnimatePresence>

          <div className="mt-8" data-id="element-2498" data-mp="3k6n0qakm36lp">
            <TrackingNotice data-id="element-2499" data-mp="3k5v2st3jkws9" />
          </div>
        </div>
      </main>
      <Footer data-id="element-2500" data-mp="3p3n3abi95r82" />
    </div>;
}